<template>
  <div v-if="noData" class="flex h-56 flex-col justify-between rounded-md border bg-white">
    <div
      :class="['flex justify-between rounded-t-md object-cover', backgroundGradient]"
      :style="background && `background-image: url(${background})`"
    >
      <img class="h-auto w-1/2 object-cover" :src="backgroundPattern" />
      <img class="my-2 mr-4 h-20 w-auto" :src="backgroundIcon" />
    </div>
    <div class="px-3 py-3 md:h-24 md:px-6">
      <h4 class="font-semibold xl:text-lg">
        {{ title }}
      </h4>
      <p v-if="subtitle" class="mb-4 h-14 text-foreground">
        {{ subtitle }}
      </p>
    </div>
    <div
      :class="`${getColorClass({ type: 'bg', value: '50' })} cursor-pointer rounded-b-md px-2`"
      @click="$emit('click')"
    >
      <div class="flex items-center justify-end text-muted-foreground">
        <span
          class="mr-2 origin-left transform cursor-pointer border-b border-transparent text-xs leading-8 transition hover:-translate-x-1 hover:scale-105 hover:border-border"
          @click="$emit('click')"
          >{{ link }}</span
        >
        <BaseIcon name="HeroPlusCircleIconSolid" class="h-4 w-4 leading-8" />
      </div>
    </div>
  </div>
  <div
    v-else
    class="flex flex-col justify-between rounded-md border bg-white"
    :class="ranking.length > 0 ? 'h-70 md:h-56' : 'h-56'"
  >
    <div class="p-3 pb-0 text-foreground md:p-6 md:pb-0">
      <h4 class="font-semibold xl:text-lg">
        {{ title }}
      </h4>
      <p
        v-if="subtitle"
        class="text-foreground underline"
        :class="truncate ? '... max-w-xs overflow-hidden truncate whitespace-nowrap' : ''"
      >
        {{ subtitle }}
      </p>
      <span v-if="hasCustomSubtitle">
        <slot name="custom-subtitle" />
      </span>
    </div>
    <div v-if="ranking.length > 0" class="flex flex-col justify-center md:h-28">
      <div
        v-for="(rankedElement, rankedElementIndex) in ranking"
        :key="rankedElementIndex"
        class="mx-2 flex flex-col items-center justify-between p-1 md:mx-6 md:flex-row"
        :class="rankedElementIndex !== ranking.length - 1 ? 'border-b' : ''"
      >
        <div class="flex items-center md:w-3/4">
          <p class="mr-2 w-4">
            {{ getRank(rankedElement.rank) }}
          </p>
          <p class="... overflow-hidden truncate whitespace-nowrap text-sm xl:text-base">
            {{ rankedElement.name }}
          </p>
        </div>
        <span :class="`text-lg ${getColorClass({ type: 'text', value: '800' })} font-bold`">{{
          rankedElement.count
        }}</span>
      </div>
    </div>
    <div
      v-else
      class="flex px-3 text-center md:px-5"
      :class="[rightLegend ? 'justify-around' : 'justify-center', subtitle ? '' : 'mt-6']"
    >
      <div>
        <p class="text-sm uppercase text-foreground-secondary">
          {{ leftLegend }}
        </p>
        <span :class="`${getColorClass({ type: 'text', value: '800' })} text-4xl font-bold`">{{ leftValue }}</span>
      </div>
      <div v-if="rightLegend" class="border-l border-border" />
      <div>
        <p class="text-sm uppercase text-foreground-secondary">
          {{ rightLegend }}
        </p>
        <span :class="`${getColorClass({ type: 'text', value: '800' })} text-4xl font-bold`">{{ rightValue }}</span>
      </div>
    </div>
    <div
      :class="`${getColorClass({ type: 'bg', value: '50' })} cursor-pointer rounded-b-md px-2`"
      @click="$emit('click')"
    >
      <div class="flex items-center justify-end text-muted-foreground">
        <span
          class="mr-2 origin-left transform cursor-pointer border-b border-transparent text-xs leading-8 transition hover:-translate-x-1 hover:scale-105 hover:border-border"
          @click="$emit('click')"
          >{{ link }}</span
        >
        <BaseIcon name="HeroArrowLongRightIconSolid" class="h-4 w-4 leading-8" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { rankingInterface } from './albumInterfaces'
import useAlbum from './useAlbum'
import { getColorClass } from '@/utils/colorClass'

export default defineComponent({
  name: 'Album',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    leftLegend: {
      type: String,
      default: ''
    },
    leftValue: {
      type: [Number, String],
      default: ''
    },
    rightLegend: {
      type: String,
      default: ''
    },
    rightValue: {
      type: [Number, String],
      default: ''
    },
    ranking: {
      type: Array as PropType<Array<rankingInterface>>,
      default: () => []
    },
    link: {
      type: String,
      default: ''
    },
    noData: {
      type: Boolean,
      default: false
    },
    background: {
      type: String,
      default: ''
    },
    backgroundPattern: {
      type: String,
      default: ''
    },
    backgroundIcon: {
      type: String,
      default: ''
    },
    truncate: {
      type: Boolean,
      default: false
    },
    backgroundGradient: {
      type: String,
      default: ''
    }
  },
  emits: ['click'],
  setup(props, context) {
    const { getRank } = useAlbum()
    const hasCustomSubtitle = !!context.slots['custom-subtitle']

    return {
      getRank,
      hasCustomSubtitle,
      getColorClass
    }
  }
})
</script>
